import { AxiosError } from 'axios';
import cookie from 'js-cookie';
import findLast from 'lodash/findLast';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { v4 } from 'uuid';

import type { RecommendedJobsChatContextInput } from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';

import {
  createSession,
  sendFeedback,
  sendMessage,
} from '../api/careerFeedAssistantApi';
import type {
  CareerFeedMessage,
  CareerFeedTextMessage,
  ErrorType,
} from '../types';

type CareerFeedAIAssistantContextType = {
  messages: CareerFeedMessage[];
  sessionId: string | null;
  candidateChatContext: RecommendedJobsChatContextInput | null;
  persistedCandidateChatContext: RecommendedJobsChatContextInput | null;
  invokeCreateSession: (jobIds: string[]) => void;
  invokeSendMessage: (message: string, skip: boolean) => void;
  invokeSendFeedback: (sentiment: boolean) => void;
  anyMessageLoading: boolean;
  conversationFinished: boolean;
  isCreateSessionLoading: boolean;
  createSessionError: ErrorType | null;
  sendMessageError: ErrorType | null;
  lastCompletedAssistantResponse: CareerFeedTextMessage | undefined;
  isAssistantResponsePending: boolean;
  persistCandidateChatContext: () => void;
  clearCandidateChatContext: () => void;
  reset: () => void;
  progress: ProgressType;
};

const CareerFeedAIAssistantContext = createContext<
  CareerFeedAIAssistantContextType | undefined
>(undefined);

const getErrorType = (e: AxiosError | Error | unknown): ErrorType => {
  if (e instanceof AxiosError) {
    if (
      e.status === 400 &&
      e.response?.data?.errorType === 'INPUT_OR_OUTPUT_BLOCKED'
    ) {
      return 'INPUT_OR_OUTPUT_BLOCKED';
    }

    if (e.status === 429) {
      return 'TOO_MANY_REQUESTS';
    }
  }

  return 'GENERIC_ERROR';
};

const CANDIDATE_CHAT_CONTEXT_COOKIE_NAME = 'candidateChatContext';

type CandidateChatContextFromCookieType = {
  candidateChatContext: CareerFeedAIAssistantContextType['candidateChatContext'];
};
const useCandidateChatContextFromCookie = () => {
  const getValue = useCallback(() => {
    const candidateChatContextString = cookie.get(
      CANDIDATE_CHAT_CONTEXT_COOKIE_NAME,
    );

    if (!candidateChatContextString) {
      return null;
    }

    try {
      return JSON.parse(
        candidateChatContextString,
      ) as CandidateChatContextFromCookieType;
    } catch (e) {
      logger.error(
        {
          err: e as Error,
        },
        'GenAI: Error occurred while reading the candidate chat context from the cookie',
      );
      return null;
    }
  }, []);

  const [data, setData] = useState<CandidateChatContextFromCookieType | null>(
    getValue(),
  );

  const set = useCallback((value: RecommendedJobsChatContextInput) => {
    const newData: CandidateChatContextFromCookieType = {
      candidateChatContext: { ...value, timestamp: new Date().toISOString() },
    };

    const oneHourLater = new Date();
    oneHourLater.setHours(oneHourLater.getHours() + 1);

    cookie.set(CANDIDATE_CHAT_CONTEXT_COOKIE_NAME, JSON.stringify(newData), {
      expires: oneHourLater,
    });

    setData(newData);
  }, []);

  const clear = useCallback(() => {
    cookie.remove(CANDIDATE_CHAT_CONTEXT_COOKIE_NAME);

    setData(null);
  }, []);

  return { data, set, clear };
};

/**
 * Progress types:
 * - `initial`: The initial state of the assistant
 * - `gatheringRefinement`: The assistant is gathering refinement information
 * - `reviewRefinement`: The assistant is reviewing the refinement information
 * - `recsRefined`: The recommendations have been refined
 * - `recsRefined-withPersistedData`: On initial, when using CandidateChatContext from cookie to refine the recommendations
 *
 * Possible flow:
 * - `initial` -> `gatheringRefinement` -> `reviewRefinement` -> `recsRefined`
 * - `recsRefined-withPersistedData` -> `initial` -> `gatheringRefinement` -> `reviewRefinement` -> `recsRefined`
 * - `recsRefined-withPersistedData` -> `gatheringRefinement` -> `reviewRefinement` -> `recsRefined`
 */
type ProgressType =
  | 'initial'
  | 'gatheringRefinement'
  | 'reviewRefinement'
  | 'recsRefined'
  | 'recsRefined-withPersistedData';

export const CareerFeedAIAssistantProvider: React.FC<{
  children: React.ReactNode;
  useAltModel: boolean;
}> = ({ children, useAltModel }) => {
  const [messages, setMessages] = useState<CareerFeedMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isCreateSessionLoading, setCreateSessionLoading] = useState(false);
  const [createSessionError, setCreateSessionError] =
    useState<ErrorType | null>(null);
  const [sendMessageError, setSendMessageError] = useState<ErrorType | null>(
    null,
  );
  const [conversationFinished, setConversationFinished] = useState(false);
  const [candidateChatContext, setCandidateChatContext] =
    useState<RecommendedJobsChatContextInput | null>(null);

  const persistedCandidateChatContext = useCandidateChatContextFromCookie();

  const reset = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setCreateSessionLoading(false);
    setCreateSessionError(null);
    setSendMessageError(null);
    setConversationFinished(false);
    setCandidateChatContext(null);
  }, []);

  const progress = useMemo<ProgressType>(() => {
    // Since on the initial state, after user starts the conversation, we will push the first request to the `messages[]`, and wait for the `sessionId` to be set.
    /**
     * Scenario                  | sessionId | messages.length | expected progress
     * --------------------------|-----------|-----------------|-------------------
     * initial                   | null      | 0               | "initial"
     * starting the conversation | null      | 1               | "initial"
     * gathering refinement      | true      | 1               | "gatheringRefinement"
     */
    if (sessionId && messages.length) {
      if (conversationFinished) {
        return 'reviewRefinement';
      }

      if (persistedCandidateChatContext?.data) {
        return 'recsRefined';
      }

      return 'gatheringRefinement';
    }

    if (!sessionId && persistedCandidateChatContext?.data) {
      return 'recsRefined-withPersistedData';
    }

    // aka !sessionId
    return 'initial';
  }, [
    conversationFinished,
    messages.length,
    persistedCandidateChatContext?.data,
    sessionId,
  ]);

  const invokeCreateSession = useCallback(
    async (jobIds: string[]) => {
      setCreateSessionError(null);

      if (!isCreateSessionLoading) {
        const messageId = v4();
        setMessages((prevMessages) => [
          ...prevMessages,
          { messageId, role: 'assistant', loading: true },
        ]);

        try {
          setCreateSessionLoading(true);
          const result = await createSession(
            {
              jobIds,
            },
            useAltModel,
          );

          setSessionId(result.sessionId);
          setMessages((prevMessages) =>
            prevMessages.map((prevMessage) => {
              if (prevMessage.messageId === messageId) {
                return {
                  ...prevMessage,
                  loading: false,
                  text: result.message,
                  suggestions: result.suggestions,
                };
              }

              return prevMessage;
            }),
          );
        } catch (e) {
          logger.error(
            {
              err: e as Error,
            },
            'GenAI: Error occurred while creating the session',
          );
          setCreateSessionError(getErrorType(e));
          setMessages((prevMessages) =>
            prevMessages.filter(
              (prevMessage) => prevMessage.messageId !== messageId,
            ),
          );
        }

        setCreateSessionLoading(false);
      }
    },
    [isCreateSessionLoading, useAltModel],
  );

  const invokeSendMessage = useCallback(
    async (message: string, skip: boolean) => {
      if (!sessionId) {
        return;
      }

      setSendMessageError(null);
      const assistantMessageId = v4();
      const userMessageId = v4();

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          messageId: userMessageId,
          role: 'user',
          loading: false,
          text: message,
        },
        {
          messageId: assistantMessageId,
          role: 'assistant',
          loading: true,
        },
      ]);

      try {
        const result = await sendMessage(sessionId, message, useAltModel, skip);
        setMessages((prevMessages) =>
          prevMessages.map((prevMessage) => {
            if (prevMessage.messageId === assistantMessageId) {
              return {
                ...prevMessage,
                loading: false,
                text: result.message,
                suggestions: result.suggestions,
                collectGeneralRequirements: result.collectGeneralRequirements,
              };
            }

            return prevMessage;
          }),
        );
        setConversationFinished(result.conversationFinished);
        setCandidateChatContext(result.candidateChatContext ?? null);
      } catch (e) {
        logger.error(
          {
            err: e as Error,
          },
          'GenAI: Error occurred while sending the message',
        );
        setSendMessageError(getErrorType(e));
        setMessages((prevMessages) =>
          prevMessages.filter(
            (prevMessage) =>
              prevMessage.messageId !== assistantMessageId &&
              prevMessage.messageId !== userMessageId,
          ),
        );
      }
    },
    [sessionId, useAltModel],
  );

  const invokeSendFeedback = useCallback(
    async (sentiment: boolean) => {
      if (!sessionId) {
        return;
      }

      try {
        await sendFeedback(sessionId, sentiment);
      } catch (e) {
        logger.error(
          {
            err: e as Error,
          },
          'GenAI: Error occurred while sending feedback',
        );
      }
    },
    [sessionId],
  );
  const anyMessageLoading = useMemo(
    () => messages.some((message) => message.loading),
    [messages],
  );

  const lastCompletedAssistantResponse = useMemo(
    () =>
      findLast(
        messages,
        (message): message is CareerFeedTextMessage =>
          message.role === 'assistant' && !message.loading,
      ),
    [messages],
  );

  const isAssistantResponsePending = useMemo(
    () =>
      findLast(messages, (message) => message.role === 'assistant')?.loading ||
      false,
    [messages],
  );

  return (
    <CareerFeedAIAssistantContext.Provider
      value={{
        messages,
        sessionId,
        invokeCreateSession,
        invokeSendMessage,
        invokeSendFeedback,
        anyMessageLoading,
        conversationFinished,
        candidateChatContext,
        isCreateSessionLoading,
        createSessionError,
        sendMessageError,
        lastCompletedAssistantResponse,
        isAssistantResponsePending,
        persistCandidateChatContext: () =>
          persistedCandidateChatContext.set(candidateChatContext!),
        clearCandidateChatContext: persistedCandidateChatContext.clear,
        persistedCandidateChatContext:
          persistedCandidateChatContext.data?.candidateChatContext || null,
        reset,
        progress,
      }}
    >
      {children}
    </CareerFeedAIAssistantContext.Provider>
  );
};

export const useCareerFeedAIAssistant = () => {
  const context = useContext(CareerFeedAIAssistantContext);
  if (!context) {
    throw new Error(
      'useCareerFeedAIAssistant must be used within a CareerFeedAIAssistantProvider',
    );
  }
  return context;
};
